import $ from 'jquery';
import { defaultHooks } from '@wordpress/hooks';
import { ACTIONS } from './constants';
import {
	mountSdkComponent,
	isRedirectComponent,
	normalizeComponentName
} from '../util/components';

const log = ( ...args ) => console.log( '[action]', ...args );

export const setLongId = ( longId ) => async ( { select, resolveSelect, dispatch } ) => {
	const sdk = await resolveSelect.getSdk();
	await sdk.updateLongId( longId );
	dispatch( { type: ACTIONS.SET_LONG_ID, payload: longId } );
};

export const setSdkState = ( sdkState ) => {
	return { type: ACTIONS.SET_SDK_STATE, payload: sdkState };
};

export function setEnv( env ) {
	return { type: ACTIONS.SET_ENV, payload: env };
}

export const pay = () => ( { select, dispatch } ) => {
	const { instance } = select.getActiveComponent();

	if ( !instance ) {
		log( 'Component not found.' );
		return;
	}
	if ( !instance.pay ) {
		log( 'Component does not have a pay method.', instance );
		return;
	}

	log( 'pay -', instance );
	instance.pay();
};

export const onBeforeError = ( sdk, component, data ) => async ( { select, dispatch } ) => {
	console.error( 'callback: onBeforeError -', { sdk, component, data } );
	dispatch.setSdkState( sdk.state );

	/**
	 * For SYSTEM_FAILURE, we assume there is nothing we can do to recover.
	 * Use hosted mode fallback and let the customer proceed
	 */
	if ( [ 'SYSTEM_FAILURE' ].includes( data?.interaction?.reason ) ) {
		dispatch.setForceHostedFlow( true );
		return true;
	}

	/**
	 * Let the outside world attempt to handle the error.
	 * The different checkout types can reboot the payment attempt from there.
	 */
	return defaultHooks.applyFilters( 'payoneer.webSdk.onSdkError', false, sdk, component, data );
};

export const onPaymentDeclined = ( sdk, component, data ) => async ( { select, dispatch } ) => {
	log( 'callback: onPaymentDeclined -', component, data );

	return defaultHooks.applyFilters( 'payoneer.webSdk.onPaymentDeclined', true, sdk, component, data );
};

export const onPaymentFailure = (sdk, component, data) => async ( { select, dispatch } ) => {
	log( 'callback: onPaymentFailure -', component, data );

	return defaultHooks.applyFilters( 'payoneer.webSdk.onPaymentFailure', true, sdk, component, data );
}

/**
 * This callback tells us about the current input-state in a specific checkout component.
 *
 * For Stripe merchants, this callback is instantly fired for _all_ payment methods, once they are
 * fully loaded and usable. This makes the callback a good place to recognize, if a component has
 * finished loading - that's why we set "isReady" is true here (and never to false again).
 *
 * For PayFac this callback only fires for the credit card component, and can be used to verify
 * it's ready in the same way. However, other BNPL components (Afterpay) do _not_ trigger this
 * callback, and readiness cannot be detected here.
 */
export const onValidationInfo = ( sdk, componentName, data ) => async ( { select, dispatch } ) => {
	log( 'callback: onValidationInfo -', componentName, data );

	const normalizedName = normalizeComponentName( componentName, data );
	dispatch.setIsValid( normalizedName, data.valid );

	// The validation-info callback indicates that the component is ready to be used.
	dispatch.setIsReady( normalizedName, true );
};

export const setActivePaymentMethod = ( method ) => async ( { dispatch } ) => {
	log( 'setActivePaymentMethod -', method );

	dispatch.mountComponents();
	dispatch( { type: ACTIONS.SET_ACTIVE_PAYMENT_METHOD, payload: method } );
};

export function setForceHostedFlow( state ) {
	log( 'setForceHostedFlow - override state:', state );
	return { type: ACTIONS.SET_FORCE_HOSTED_FLOW, payload: state };
}

export const setOnPaymentUnsuccessfulNonce = ( nonce ) => ( { dispatch } ) => {
	dispatch( { type: ACTIONS.SET_ON_PAYMENT_UNSUCCESSFUL_NONCE, payload: nonce } );
};

export const setOrderId = ( orderId ) => ( { dispatch } ) => {
	dispatch( { type: ACTIONS.SET_ORDER_ID, payload: orderId } );
};

export const releaseOnHoldOrder = ( requestData ) => async ( { dispatch } ) => {
	log( 'releaseOnHoldOrder -', requestData );

	const targetUrlBase = window.wc_checkout_params.wc_ajax_url.toString().replace( '%%endpoint%%', 'payoneer-checkout-payment-unsuccessful' );
	const separator = targetUrlBase.includes( '?' ) ? '&' : '?';
	const targetUrl = targetUrlBase + separator + '_ajax_nonce=' + window.PayoneerData.onPaymentUnsuccessfulNonce;

	return new Promise( ( resolve, reject ) => {
		$.ajax( {
			type: 'POST', url: targetUrl, xhrFields: {
				// This is important. We need the session cookie to access the LIST in the back-end
				withCredentials: true,
			}, data: requestData, dataType: 'json',
		} ).success( ( data ) => {
			// New nonce for the next request.
			dispatch.setOnPaymentUnsuccessfulNonce( data.nonce );
			resolve( data );
		} ).catch( ( e ) => {
			console.error( e );
			reject( e );
		} );
	} );
};

// Component handling.

export const onComponentListChange = ( sdk, changeInfo ) => async ( { select, dispatch } ) => {
	log( 'callback: onComponentListChange', sdk, changeInfo );

	changeInfo.addedComponents.forEach( ( name ) => {
		dispatch.addComponentToContainer( name );
	} );

	changeInfo.removedComponents.forEach( ( name ) => {
		dispatch.removeComponent( name );
	} );
};

export const addComponentToContainer = ( componentName ) =>
	async ( { select, resolveSelect, dispatch } ) => {
		const sdk = await resolveSelect.getSdk();
		const component = select.getComponentByName( componentName );

		if ( !component.instance || !sdk.isDroppedIn( componentName ) ) {
			try {
				const instance = sdk.dropIn( componentName, { hidePaymentButton: true } );
				dispatch.registerComponent( componentName, instance );
			} catch ( e ) {
				console.error( `Failed to drop in component ${componentName}. Error details: ${e}` );
			}
		}

		const { instance } = select.getComponentByName( componentName );
		mountUnknownComponent( componentName, instance, sdk, dispatch );
	};

export const removeComponent = ( componentName ) =>
	async ( { select, resolveSelect, dispatch } ) => {
		const sdk = await resolveSelect.getSdk();
		sdk.remove(componentName);
		dispatch.unregisterComponent( componentName );
	};

export const registerComponent = ( componentName, instance ) => {
	return { type: ACTIONS.ADD_COMPONENT, payload: { componentName, instance } };
};

export const unregisterComponent = ( componentName ) => {
	return { type: ACTIONS.REMOVE_COMPONENT, payload: { componentName } };
};

/**
 * When a React component is displayed for the first time, the SDK needs to mount its dropIn.
 * So they call this function in a useEffect callback.
 * However:
 * If the SDK has been bootstrapped already, and a new method becomes available after
 * checkout field changes we need to instruct the SDK to refetch the list of dropIn components.
 * This is why we call sdk.updateLongId()
 * After that, the onComponentListChange handler should take care of mounting automatically
 *
 * @returns {(function({select: *, resolveSelect: *, dispatch: *}): Promise<void>)|*}
 */
export const initPayoneerComponents = () => async ( { select, resolveSelect, dispatch } ) => {
	log( 'initPayoneerComponents' );

	const sdk = await resolveSelect.getSdk();
	const longId = await resolveSelect.getLongId();
	await sdk.updateLongId( longId );

	await dispatch.mountComponents();
};

export const reboot = () => async ( { select, resolveSelect, dispatch } ) => {
	log( 'reboot' );
	dispatch.unmountAllComponents();

	defaultHooks.doAction( 'payoneer.webSdk.reboot' );
	wp.data.dispatch( 'wc/store/cart' ).invalidateResolutionForStore();
	dispatch.invalidateResolutionForStore();

	await dispatch.mountComponents();
};

export const mountComponents = () => async ( { select, resolveSelect, dispatch } ) => {
	log( 'mountComponents' );
	dispatch.unmountAllComponents();

	const sdk = await resolveSelect.getSdk();
	const components = select.getAllComponents();

	Object.entries( components ).forEach( async ( [ componentName, item ] ) => {
		mountUnknownComponent( componentName, item.instance, sdk, dispatch );
	} );
};

/**
 * Mount a component which can be either a PayFac or a Stripe component.
 *
 * PayFac components do not expose all callback events that are required by Stripe components
 * as they implement a few methods as "redirect" components that have no input or validation.
 * This helper checks, if a mounted component is such a "redirect" component - if it is, then
 * it's instantly marked as ready and valid, as there's no iframe to wait for.
 */
const mountUnknownComponent = ( componentName, instance, sdk, dispatch ) => {
	mountSdkComponent( componentName, instance, sdk );
	dispatch.setIsMounted( componentName, true );

	if ( isRedirectComponent( instance ) ) {
		log( 'mark redirect component as valid and ready -', componentName );

		dispatch.setIsReady( componentName, true );
		dispatch.setIsValid( componentName, true );
	}
};

export const unmountAllComponents = () => ( { select, dispatch } ) => {
	const components = select.getAllComponents();

	Object.keys( components ).forEach( ( componentName ) => {
		dispatch.setIsMounted( componentName, false );
	} );
};

export const setIsValid = ( componentName, flag ) => {
	return { type: ACTIONS.SET_IS_VALID, payload: { componentName, flag } };
};

export const setIsMounted = ( componentName, flag = true ) => {
	return { type: ACTIONS.SET_IS_MOUNTED, payload: { componentName, flag } };
};

export const setIsReady = ( componentName, flag ) => {
	return { type: ACTIONS.SET_IS_READY, payload: { componentName, flag } };
};
